import React, { useEffect, useRef, useState } from "react";
import uploadStudioFiles from "./studioUpload";
import { ACCENT } from "./shared";

export const DOC_TYPES = ["image/jpeg", "image/png", "application/pdf"];
export const DOC_MAX_BYTES = 10 * 1024 * 1024;
export const DOC_RULE_MESSAGE = "Please upload a JPG, PNG, or PDF file smaller than 10 MB.";
const MIN_IMAGE_SIDE = 400; // anything smaller can't be a readable photo of an ID

export const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

async function sha256Hex(file) {
  if (!window.crypto?.subtle || typeof file.arrayBuffer !== "function") return null; // very old browser / insecure origin: the API check is skipped
  try {
    const digest = await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    return null;
  }
}

function imageSize(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

// Returns an error message, or null when the file can be uploaded.
export async function checkDocument(file) {
  if (!DOC_TYPES.includes(file.type) || file.size > DOC_MAX_BYTES) return DOC_RULE_MESSAGE;
  if (file.size === 0) return "This file is empty. Please choose another one.";
  if (file.type.startsWith("image/")) {
    const size = await imageSize(file);
    if (!size) return "This image could not be read. Please upload a clear, uncropped photo or scan.";
    if (Math.max(size.width, size.height) < MIN_IMAGE_SIDE) {
      return "This image is too small to read. Please upload a clearer, uncropped photo or scan.";
    }
  }
  return null;
}

/**
 * One document upload box: pick a file, see progress, then its name and size (and a
 * preview for images), with Remove / Replace. `value` is {url, name, size, type, hash, preview}
 * or null; the parent keeps it in form state. `error` is a message from the parent (required etc.).
 */
export default function DocumentSlot({ label, value, onChange, error, required, disabled, hint, otherHashes = [] }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // null = idle, 0..1 = uploading
  const [localError, setLocalError] = useState("");
  const previewRef = useRef(null);

  useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setLocalError("");
    const problem = await checkDocument(file);
    if (problem) {
      setLocalError(problem);
      return;
    }
    const hash = await sha256Hex(file);
    if (hash && otherHashes.includes(hash)) {
      setLocalError("This file was already added. Upload a different document.");
      return;
    }
    setProgress(0);
    try {
      const [uploaded] = await uploadStudioFiles([file], { onProgress: setProgress });
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      previewRef.current = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
      onChange({ url: uploaded.url, name: file.name, size: file.size, type: file.type, hash, preview: previewRef.current });
    } catch (e) {
      setLocalError(e.message || "Upload failed. Please try again.");
    } finally {
      setProgress(null);
    }
  };

  const shownError = localError || error;
  const uploading = progress !== null;

  return (
    <div>
      {label && (
        <p className="text-sm font-semibold text-gray-900 mb-1">
          {label}
          {required && <span className="text-red-600"> *</span>}
        </p>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={pick} />
      <div className={`rounded-xl border bg-white px-4 py-3 ${shownError ? "border-red-400" : "border-gray-200"}`}>
        {uploading ? (
          <div role="status" aria-live="polite">
            <p className="text-sm text-gray-600 mb-2">Uploading… {Math.round(progress * 100)}%</p>
            <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: ACCENT }} />
            </div>
          </div>
        ) : value ? (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center text-xs font-bold text-gray-500 shrink-0">
              {value.preview ? <img src={value.preview} alt="" className="w-full h-full object-cover" /> : value.type === "application/pdf" ? "PDF" : "FILE"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{value.name}</p>
              <p className="text-xs text-gray-500">
                {value.size ? `${formatBytes(value.size)} · ` : ""}
                <span style={{ color: ACCENT }}>Uploaded</span>
              </p>
            </div>
            <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} className="text-sm font-semibold disabled:opacity-50" style={{ color: ACCENT }}>
              Replace
            </button>
            <button type="button" disabled={disabled} onClick={() => { setLocalError(""); onChange(null); }} className="text-sm text-gray-500 underline disabled:opacity-50">
              Remove
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-500">{hint || "JPG, PNG or PDF, up to 10 MB"}</span>
            <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} className="text-sm font-bold disabled:opacity-50" style={{ color: ACCENT }}>
              Browse
            </button>
          </div>
        )}
      </div>
      {shownError && <p role="alert" className="text-red-600 text-sm mt-1">{shownError}</p>}
    </div>
  );
}
